import React, { useState } from 'react';
import { useTransferHistory } from '../hooks/useTransfers';
import { RefreshCw, FileText, Download, Copy, ExternalLink } from 'lucide-react';
import { useTimezone, formatTimestampWithTimezone } from '../contexts/TimezoneContext';
import BalanceHistory from './BalanceHistory';
import apiClient from '../services/apiClient';
import './TransferHistory.css';

const EXCHANGES = [
  { id: '', name: 'All Exchanges' },
  { id: 'binance', name: 'Binance' },
  { id: 'bybit', name: 'Bybit' },
  { id: 'okx', name: 'OKX' },
  { id: 'kucoin', name: 'KuCoin' },
  { id: 'gateio', name: 'Gate.io' },
  { id: 'woox', name: 'WOO X' },
  { id: 'hyperliquid', name: 'Hyperliquid' },
];

const ASSETS = [
  { id: '', name: 'All Assets' },
  { id: 'USDT', name: 'USDT' },
  { id: 'USDC', name: 'USDC' },
];

// Known treasury wallets
const TREASURY_WALLETS = {
  '0xdd0c78a74e27c1ec013321e41189595459d24884': 'Treasury [Main]',
};

// Explorer URLs by chain
const EXPLORER_URLS = {
  arbitrum: 'https://arbiscan.io',
  ethereum: 'https://etherscan.io',
};

const formatDate = (dateString, timezone) => {
  if (!dateString) return '-';
  return formatTimestampWithTimezone(dateString, {
    timezone: timezone || 'UTC',
    includeDate: true,
    includeTime: true,
    format: 'full',
  }) || '-';
};

const formatAmount = (amount) => {
  if (!amount) return '-';
  return parseFloat(amount).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Get display name for exchange/wallet
const getDisplayName = (exchange) => {
  if (!exchange) return '-';
  const lowerAddr = exchange.toLowerCase();
  if (TREASURY_WALLETS[lowerAddr]) {
    return TREASURY_WALLETS[lowerAddr];
  }
  // Capitalize exchange names
  const names = {
    binance: 'Binance',
    bybit: 'Bybit',
    okx: 'OKX',
    kucoin: 'KuCoin',
    gateio: 'Gate.io',
    woox: 'WOO X',
    hyperliquid: 'Hyperliquid',
  };
  return names[exchange] || exchange;
};

// Check if value is an address
const isAddress = (value) => value && value.startsWith('0x') && value.length === 42;

// Copy to clipboard
const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text);
};

// Get explorer URL for tx
const getExplorerUrl = (txHash, chain = 'arbitrum') => {
  const baseUrl = EXPLORER_URLS[chain] || EXPLORER_URLS.arbitrum;
  return `${baseUrl}/tx/${txHash}`;
};

// Compact status badge
const StatusBadge = ({ status }) => {
  const config = {
    pending: { label: 'PENDING', className: 'status-pending' },
    validated: { label: 'VALIDATED', className: 'status-pending' },
    withdrawing: { label: 'WITHDRAWING', className: 'status-info' },
    in_treasury: { label: 'IN TREASURY', className: 'status-info' },
    swapping: { label: 'SWAPPING', className: 'status-info' },
    depositing: { label: 'DEPOSITING', className: 'status-info' },
    completed: { label: '✓', className: 'status-completed' },
    failed: { label: '✗ FAILED', className: 'status-failed' },
    rejected: { label: '✗ REJECTED', className: 'status-failed' },
    cancelled: { label: 'CANCELLED', className: 'status-cancelled' },
  };
  const { label, className } = config[status?.toLowerCase()] || config.pending;
  return <span className={`status-badge-compact ${className}`}>{label}</span>;
};

function TransferList({ onSelectTransfer }) {
  const { activeTimezone } = useTimezone();
  const [filters, setFilters] = useState({
    exchange: '',
    asset: '',
    limit: 20,
    offset: 0,
  });

  const { transfers, pagination, isLoading, isError, error, refresh } = useTransferHistory(
    {
      exchange: filters.exchange || undefined,
      asset: filters.asset || undefined,
      limit: filters.limit,
      offset: filters.offset,
    },
    60000 // Refresh every 60 seconds
  );

  const [showFilters, setShowFilters] = useState(false);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
      offset: 0, // Reset pagination on filter change
    }));
  };

  const handlePageChange = (newOffset) => {
    setFilters((prev) => ({
      ...prev,
      offset: newOffset,
    }));
  };

  const handleExportCSV = () => {
    if (!transfers || transfers.length === 0) return;

    const columns = ['ID', 'Date', 'Route', 'Amount', 'Status', 'Tx Hash'];
    let csvContent = columns.join(',') + '\n';

    transfers.forEach(t => {
      const row = [
        t.id,
        new Date(t.createdAt).toISOString(),
        `"${t.source.exchange} -> ${t.destination.exchange}"`,
        `${t.source.amount} ${t.source.asset}`,
        t.status,
        t.steps?.[0]?.txHash || ''
      ];
      csvContent += row.join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `transfers_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totalPages = pagination ? Math.ceil(pagination.total / filters.limit) : 1;
  const currentPage = Math.floor(filters.offset / filters.limit) + 1;

  return (
    <>
      <div className="history-header">
        <div className="header-left">
          <h3>Transfer History</h3>
        </div>
        <div className="header-actions">
          <button onClick={handleExportCSV} className="btn-export" disabled={!transfers?.length}>
            <Download size={14} />
            <span>Export CSV</span>
          </button>
          <button onClick={refresh} className="btn-refresh" disabled={isLoading}>
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* Filters Toggle Button (Mobile) */}
      <div className="history-filters-toggle">
        <button 
          className="btn-filter-toggle" 
          onClick={() => setShowFilters(!showFilters)}
        >
          <span>Filters</span>
          <span className={`filter-toggle-icon ${showFilters ? 'open' : ''}`}>▼</span>
        </button>
      </div>

      {/* Filters */}
      <div className={`history-filters ${showFilters ? 'show-filters' : ''}`}>
        <div className="filter-group">
          <label htmlFor="exchange">Exchange</label>
          <select
            id="exchange"
            name="exchange"
            value={filters.exchange}
            onChange={handleFilterChange}
          >
            {EXCHANGES.map((ex) => (
              <option key={ex.id} value={ex.id}>
                {ex.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="asset">Asset</label>
          <select
            id="asset"
            name="asset"
            value={filters.asset}
            onChange={handleFilterChange}
          >
            {ASSETS.map((asset) => (
              <option key={asset.id} value={asset.id}>
                {asset.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="limit">Per Page</label>
          <select
            id="limit"
            name="limit"
            value={filters.limit}
            onChange={handleFilterChange}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>

      {/* Error State */}
      {isError && (
        <div className="history-error">
          <p>Failed to load transfer history: {error?.message}</p>
          <button onClick={refresh}>Retry</button>
        </div>
      )}

      {/* Loading State */}
      {isLoading && transfers.length === 0 && (
        <div className="history-loading">
          <div className="loading-spinner"></div>
          <span>Loading transfers...</span>
        </div>
      )}

      {/* Transfer Table */}
      {!isError && transfers.length > 0 && (
        <>
          <div className="history-table-wrapper">
            <table className="history-table">
              <thead>
                <tr>
                  <th>Date/Time</th>
                  <th>Route</th>
                  <th>Status</th>
                  <th>Sent</th>
                  <th>Received</th>
                  <th>Fees</th>
                  <th>ID</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {transfers.map((transfer) => {
                  const srcExchange = transfer.source?.exchange;
                  const dstExchange = transfer.destination?.exchange;
                  const srcName = getDisplayName(srcExchange);
                  const dstName = getDisplayName(dstExchange);
                  const sentAmount = parseFloat(transfer.source?.amount || 0);
                  const asset = transfer.source?.asset || 'USDT';
                  const chain = transfer.source?.chain || 'arbitrum';
                  
                  // Get tx hash from onchain transfer step
                  const onchainStep = transfer.steps?.find(s => s.action === 'onchain_transfer' && s.txHash);
                  const withdrawStep = transfer.steps?.find(s => s.action === 'cex_withdraw');
                  
                  // Use values from API if available, otherwise calculate from steps
                  let receivedAmount = transfer.receivedAmount ? parseFloat(transfer.receivedAmount) : null;
                  let totalFee = transfer.totalFee ? parseFloat(transfer.totalFee) : null;
                  
                  // Fallback: calculate from steps if DB values not available
                  // Only calculate for completed transfers (not failed/rejected)
                  if (receivedAmount === null && transfer.status === 'completed' && transfer.status !== 'failed' && transfer.status !== 'rejected') {
                    // For completed transfers, try to get received amount from the last step
                    // Priority: cex_deposit amount > onchain_transfer amount > calculate from fees
                    const depositStep = transfer.steps?.find(s => s.action === 'cex_deposit' && s.status === 'completed');
                    const onchainStep = transfer.steps?.find(s => s.action === 'onchain_transfer' && s.status === 'completed');
                    
                    if (depositStep?.amount) {
                      // Use the amount from the deposit step (this is what was actually received)
                      receivedAmount = parseFloat(depositStep.amount);
                      // Calculate total fee as difference
                      const stepFees = transfer.steps?.reduce((sum, step) => sum + parseFloat(step.fee || 0), 0) || 0;
                      totalFee = sentAmount - receivedAmount;
                    } else if (onchainStep?.amount) {
                      // Use the amount from the onchain transfer step
                      receivedAmount = parseFloat(onchainStep.amount);
                      // Calculate total fee as difference
                      const stepFees = transfer.steps?.reduce((sum, step) => sum + parseFloat(step.fee || 0), 0) || 0;
                      totalFee = sentAmount - receivedAmount;
                    } else {
                      // Fallback: calculate from step fees
                      const stepFees = transfer.steps?.reduce((sum, step) => sum + parseFloat(step.fee || 0), 0) || 0;
                      if (stepFees > 0) {
                        totalFee = stepFees;
                        receivedAmount = sentAmount - stepFees;
                      } else if (withdrawStep?.fee) {
                        // Just use withdraw step fee
                        totalFee = parseFloat(withdrawStep.fee);
                        receivedAmount = sentAmount - totalFee;
                      }
                    }
                  }
                  
                  // Get tx hash from onchain transfer step (this is the actual blockchain tx)
                  const txHash = onchainStep?.txHash || transfer.steps?.find(s => s.txHash)?.txHash;
                  
                  return (
                    <tr key={transfer.id}>
                      <td className="cell-date">{formatDate(transfer.createdAt, activeTimezone)}</td>
                      <td className="cell-route">
                        {(() => {
                          // Check if this is an exchange-to-exchange transfer (via treasury)
                          const srcIsExchange = !isAddress(srcExchange);
                          const dstIsExchange = !isAddress(dstExchange);
                          const isExchangeToExchange = srcIsExchange && dstIsExchange;
                          
                          // Get treasury address from withdraw step
                          const treasuryAddress = withdrawStep?.toAddress?.toLowerCase();
                          const treasuryName = treasuryAddress ? (TREASURY_WALLETS[treasuryAddress] || 'Treasury EOA') : 'Treasury EOA';
                          
                          return (
                            <span className="route-inline">
                              <span className="route-endpoint">
                                {srcName}
                                {isAddress(srcExchange) && (
                                  <Copy 
                                    size={12} 
                                    className="copy-icon" 
                                    onClick={(e) => { e.stopPropagation(); copyToClipboard(srcExchange); }}
                                    title="Copy address"
                                  />
                                )}
                              </span>
                              {isExchangeToExchange && (
                                <>
                                  <span className="route-arrow">→</span>
                                  <span className="route-endpoint treasury">
                                    {treasuryName}
                                  </span>
                                </>
                              )}
                              <span className="route-arrow">→</span>
                              <span className="route-endpoint">
                                {dstName}
                                {isAddress(dstExchange) && (
                                  <Copy 
                                    size={12} 
                                    className="copy-icon" 
                                    onClick={(e) => { e.stopPropagation(); copyToClipboard(dstExchange); }}
                                    title="Copy address"
                                  />
                                )}
                              </span>
                            </span>
                          );
                        })()}
                      </td>
                      <td className="cell-status">
                        <StatusBadge status={transfer.status} />
                      </td>
                      <td className="mono cell-amount">{formatAmount(sentAmount)} {asset}</td>
                      <td className="mono cell-amount received">
                        {receivedAmount !== null ? `${formatAmount(receivedAmount)} ${asset}` : '–'}
                      </td>
                      <td className="mono cell-fee">
                        {totalFee !== null && totalFee > 0 ? formatAmount(totalFee) : '–'}
                      </td>
                      <td className="mono cell-id">{transfer.id?.slice(0, 8)}...</td>
                      <td className="cell-actions">
                        {txHash ? (
                          <a 
                            href={getExplorerUrl(txHash, chain)} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="btn-explorer"
                            title="View on Arbiscan"
                          >
                            <ExternalLink size={14} />
                            <span>Explorer</span>
                          </a>
                        ) : (
                          <span className="no-tx">–</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && totalPages > 1 && (
            <div className="history-pagination">
              <button
                onClick={() => handlePageChange(filters.offset - filters.limit)}
                disabled={filters.offset === 0}
                className="btn-page"
              >
                ← Previous
              </button>
              <span className="page-info">
                Page {currentPage} of {totalPages}
                {pagination.total && ` (${pagination.total} total)`}
              </span>
              <button
                onClick={() => handlePageChange(filters.offset + filters.limit)}
                disabled={currentPage >= totalPages}
                className="btn-page"
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {/* Empty State */}
      {!isLoading && !isError && transfers.length === 0 && (
        <div className="history-empty">
          <div className="empty-icon"><FileText size={32} /></div>
          <p>No transfers found</p>
          <span className="empty-hint">
            {filters.exchange || filters.asset
              ? 'Try adjusting your filters'
              : 'Initiate a transfer to get started'}
          </span>
        </div>
      )}
    </>
  );
}

function TransferHistory({ onSelectTransfer }) {
  const [activeTab, setActiveTab] = useState('transfers');

  return (
    <div className="transfer-history">
      <div className="history-tabs">
        <button 
          className={activeTab === 'transfers' ? 'active' : ''} 
          onClick={() => setActiveTab('transfers')}
        >
          Transfer History
        </button>
        <button 
          className={activeTab === 'balances' ? 'active' : ''} 
          onClick={() => setActiveTab('balances')}
        >
          Balance History
        </button>
      </div>

      {activeTab === 'transfers' ? (
        <TransferList onSelectTransfer={onSelectTransfer} />
      ) : (
        <BalanceHistory />
      )}
    </div>
  );
}

export default TransferHistory;
